/**
 * Typed wrappers for the `/salescoach/api/briefs/*` endpoints (Phase 4 Plan 08 Task 1).
 *
 * Mirrors `operations-api.ts` / `quality-api.ts` conventions:
 *   1. `buildQuery(filters)` strips defaults + resolves preset → from/to.
 *   2. Granularity is appended as an extra param on the trend endpoint.
 *   3. Return type is always the raw `ApiEnvelope<T>` — unwrapping is the
 *      hook's job (`use-briefs.ts`).
 *
 * Five endpoints per Plan 06 backend (`src/dashboard/routes/api/briefs.ts`):
 *   GET /kpis              → BriefsKpis
 *   GET /trend             → BriefTrendPoint[]        (granularity)
 *   GET /log               → BriefLogRow[]
 *   GET /failure-reasons   → BriefFailureReason[]
 *   GET /complexity-dist   → BriefComplexityCell[]
 *
 * Locked per BLOCKER-06 (Plan 03): the backend only emits the
 * three-status enum (`sent | failed | skipped`) and the four-bucket
 * complexity enum (`simple | medium | complex | unknown`). The types live in
 * `api-types.ts` — this file never redeclares them.
 */

import { apiFetch } from '@/services/api';
import { buildApiUrl } from '@/services/api-client';
import type { Filters } from '@/lib/filters-schema';
import type {
  ApiEnvelope,
  BriefComplexityCell,
  BriefFailureReason,
  BriefLogRow,
  BriefMessages,
  BriefTrendPoint,
  BriefsKpis,
} from './api-types';

// ---------------------------------------------------------------------------
// Endpoint wrappers
// ---------------------------------------------------------------------------

export async function fetchBriefsKpis(
  filters: Filters,
): Promise<ApiEnvelope<BriefsKpis>> {
  return apiFetch<ApiEnvelope<BriefsKpis>>(
    buildApiUrl('/salescoach/api/briefs/kpis', filters),
  );
}

export async function fetchBriefTrend(
  filters: Filters,
  granularity: Filters['granularity'],
): Promise<ApiEnvelope<BriefTrendPoint[]>> {
  return apiFetch<ApiEnvelope<BriefTrendPoint[]>>(
    buildApiUrl('/salescoach/api/briefs/trend', filters, { granularity }),
  );
}

export async function fetchBriefLog(
  filters: Filters,
): Promise<ApiEnvelope<BriefLogRow[]>> {
  return apiFetch<ApiEnvelope<BriefLogRow[]>>(
    buildApiUrl('/salescoach/api/briefs/log', filters),
  );
}

export async function fetchBriefFailureReasons(
  filters: Filters,
): Promise<ApiEnvelope<BriefFailureReason[]>> {
  return apiFetch<ApiEnvelope<BriefFailureReason[]>>(
    buildApiUrl('/salescoach/api/briefs/failure-reasons', filters),
  );
}

export async function fetchBriefComplexityDist(
  filters: Filters,
): Promise<ApiEnvelope<BriefComplexityCell[]>> {
  return apiFetch<ApiEnvelope<BriefComplexityCell[]>>(
    buildApiUrl('/salescoach/api/briefs/complexity-dist', filters),
  );
}

// ---------------------------------------------------------------------------
// Inline brief-message viewer (Phase 8) — `:userId/:date/messages`
// ---------------------------------------------------------------------------

/**
 * Fetch the persisted Slack content for one RM on one date. The endpoint is
 * NOT wrapped in `ApiEnvelope` — it returns the row body directly (or a 404
 * envelope with `{ error: 'not_found', … }` from `apiFetch` → `ApiError`).
 *
 * Both path segments are URL-encoded so usernames containing dots / dashes
 * stay correct under Express's path parser.
 */
export async function fetchBriefMessages(
  userId: string,
  date: string,
): Promise<BriefMessages> {
  return apiFetch<BriefMessages>(
    `/salescoach/api/briefs/${encodeURIComponent(userId)}/${encodeURIComponent(date)}/messages`,
  );
}

/**
 * Fetch the persisted Slack content for a brief by its trace id. Same payload
 * as `fetchBriefMessages`, keyed on `trace_id` instead of (username, date).
 * Used by trace-centric surfaces (Feedback / Operations / any TraceDetailPanel)
 * to render the full delivered brief in place of the trace's status string.
 * 404 when the trace has no persisted brief row (non-brief trace, or older
 * than the backfill window).
 */
export async function fetchBriefMessagesByTrace(
  traceId: string,
): Promise<BriefMessages> {
  return apiFetch<BriefMessages>(
    `/salescoach/api/briefs/by-trace/${encodeURIComponent(traceId)}/messages`,
  );
}
